/**
 * Webbläsarfel (DOMException m.m.) får aldrig visas för användaren – de är på
 * engelska och säger inget vettigt. Här översätts de till korta svenska rader.
 */
const FRIENDLY_FALLBACK = "Något gick fel med skanningen. Försök igen.";

export type ScanFailureStage = "metadata" | "frames" | "frame-upload" | "compress" | "video-upload" | "queue" | "analysis";
export type ScanFailureDiagnostic = {
  stage: ScanFailureStage;
  code: string;
  name: string;
  message: string;
  userAgent: string;
  file: { name: string; type: string; bytes: number };
  at: string;
};

export function scanFailureDiagnostic(error: unknown, stage: ScanFailureStage, file: File): ScanFailureDiagnostic {
  const value = error instanceof Error ? error : new Error(String(error));
  const explicitCode = (value as Error & { code?: unknown }).code;
  return {
    stage,
    code: typeof explicitCode === "string" ? explicitCode : `${stage.toUpperCase()}_${value.name || "ERROR"}`,
    name: value.name || "Error",
    message: value.message || String(error),
    userAgent: typeof navigator === "undefined" ? "unknown" : navigator.userAgent,
    file: { name: file.name, type: file.type, bytes: file.size },
    at: new Date().toISOString(),
  };
}

export function friendlyScanError(err: unknown): string {
  if (typeof err === "string" && isSwedish(err)) return err;

  if (err instanceof Error) {
    const name = err.name;
    const message = err.message ?? "";

    if (
      name === "NotAllowedError" ||
      name === "SecurityError" ||
      /not allowed by the user agent|denied permission|user gesture/i.test(message)
    ) {
      return "Vi kunde inte läsa filmen i den här webbläsaren. Prova att välja filmen från kamerarullen i stället.";
    }
    if (name === "NotSupportedError" || /codec|not supported|decode/i.test(message)) {
      return "Filmformatet stöds inte här. Spela in en ny film med telefonens kamera och prova igen.";
    }
    if (name === "NotFoundError" || name === "NotReadableError") {
      return "Filmen kunde inte läsas. Välj filmen från kamerarullen och prova igen.";
    }
    if (/network|failed to fetch|load failed/i.test(message)) {
      return "Nätverket tappade kontakten. Kontrollera uppkopplingen och prova igen.";
    }
    // Våra egna svenska felmeddelanden får gå rakt igenom.
    if (isSwedish(message)) return message;
  }

  return FRIENDLY_FALLBACK;
}

export function scanFailureAction(diagnostic: ScanFailureDiagnostic): string {
  if (diagnostic.stage === "frames") return "Filmen kunde inte avkodas bild för bild. Välj originalfilmen från kamerarullen och försök igen.";
  if (diagnostic.stage === "frame-upload" || diagnostic.stage === "video-upload") return "Överföringen bröts. Kontrollera anslutningen och försök igen.";
  return friendlyScanError(new DOMException(diagnostic.message, diagnostic.name));
}

/** Grov men tillräcklig koll: våra egna texter är svenska, webbläsarens engelska. */
function isSwedish(text: string): boolean {
  if (!text.trim()) return false;
  if (/[åäöÅÄÖ]/.test(text)) return true;
  return /\b(kunde|filmen|bilder|skanning|försök|prylar|misslyckades|välj|max)\b/i.test(text);
}
